import SearchInput from "@/components/common/search-input";
import { useGetApplicantUsersAsInfinite } from "@/queries/users/users.api";
import type { EntityAssociateType } from "@/schemas/digital-files";
import type { UserType } from "@/types/entities/users";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
} from "@elaniintc/goes-design-system";
import { useMemo, useState, useRef, useEffect } from "react";
import { useDebounceValue } from "usehooks-ts";

interface Props {
  label: string;
  isDisabled?: boolean;
  name?: string;
  required?: boolean;
  selectedUser?: UserType | EntityAssociateType[] | null;
  defaultUser?: string | null;
  onSelectUser: (user: UserType) => void;
  placeholder?: string;
  labelClassName?: string;
  showCount?: boolean;
  count?: number;
}

export const UserApplicantSearchSelector = ({
  label,
  isDisabled = false,
  name,
  required = false,
  selectedUser,
  onSelectUser,
  defaultUser,
  placeholder = "Buscar usuario...",
  labelClassName = "text-sm",
  showCount = false,
  count = 0,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [deferredQuery] = useDebounceValue(searchQuery || "", 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useGetApplicantUsersAsInfinite({
      page: currentPage,
      ...(deferredQuery ? { name: deferredQuery } : {}),
    });

  const isLoading = !data;

  const _data = useMemo(() => {
    if (!data) return [];
    return data.pages
      .flatMap((page) => page.data)
      .filter((user) => {
        if (Array.isArray(selectedUser)) {
          return !selectedUser.some(
            (selUser) => selUser.applicant_id === user.id,
          );
        }
        return !(user.id === selectedUser?.id || user.id === defaultUser);
      });
  }, [data, selectedUser, defaultUser]);

  const apiEmpty = (data?.pages?.[0].data.length || 0) === 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectUser = (user: UserType) => {
    onSelectUser(user);
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label
        className={cn(
          "font-semibold text-body-loud leading-2xl flex justify-between",
          labelClassName,
          isDisabled && "text-on-disabled!",
        )}
        htmlFor={name}
      >
        {label}{" "}
        {required && (
          <span
            className={cn(
              "text-sm text-danger",
              isDisabled && "text-on-disabled!",
            )}
          >
            *
          </span>
        )}
        <span className="text-body-quiet font-normal text-sm">Opcional</span>
      </label>
      <div className="relative">
        <SearchInput
          ref={inputRef}
          className="w-full text-size-body!"
          variant="md"
          placeholder={placeholder}
          value={searchQuery}
          showCount={showCount}
          count={count}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!isOpen) {
              setIsOpen(true);
              setCurrentPage(1);
            }
          }}
          onFocus={() => {
            setIsOpen(true);
            setCurrentPage(1);
          }}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 rounded-lg border border-neutral-silent text-body-loud font-semibold bg-neutral-object shadow-lg">
          <div className="max-h-55 overflow-auto pt-2">
            <Content
              isLoading={isLoading}
              _data={_data}
              isFetching={isFetching}
              hasNextPage={hasNextPage}
              fetchNextPage={() => {
                fetchNextPage();
              }}
              isEmpty={_data.length === 0}
              apiEmpty={apiEmpty}
              onSelectUser={handleSelectUser}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Content = ({
  isLoading,
  _data,
  isFetching,
  hasNextPage,
  fetchNextPage,
  isEmpty,
  apiEmpty = false,
  onSelectUser,
}: {
  isLoading: boolean;
  apiEmpty?: boolean;
  isEmpty: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  _data: UserType[];
  isFetching: boolean;
  onSelectUser: (user: UserType) => void;
}) => {
  if (isLoading) {
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <Icon
          type="far"
          name="spinner-third"
          className="text-on-disabled animate-spin text-2xl"
        />
      </div>
    );
  }

  if (hasNextPage && _data.length === 0) {
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <Button
          variant="ghost-blue"
          size="xs"
          type="button"
          disabled={isFetching}
          onClick={() => {
            if (isFetching) return;
            fetchNextPage();
          }}
        >
          Más resultados
          <Icon
            type="far"
            name={isFetching ? "spinner-third" : "plus"}
            className={cn("text-current", isFetching && "animate-spin")}
          />
        </Button>
      </div>
    );
  }

  if (apiEmpty) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay usuarios disponibles
        </span>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay más resultados
        </span>
      </div>
    );
  }

  return (
    <>
      <ul className="px-2 min-h-32">
        {_data?.map((user, index) => {
          return (
            <li key={`${user.id}-${user.keycloak_id}-${index}`}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
                onClick={() => onSelectUser(user)}
              >
                <Avatar size="2xs">
                  <AvatarFallback
                    color="action"
                    className="pointer-events-none"
                  >
                    {user.first_name?.charAt(0).toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 flex items-center gap-2 text-base leading-3xl truncate pointer-events-none select-none">
                  <span className="text-body font-semibold whitespace-nowrap">{`${user.first_name} ${user.last_name}`}</span>
                  <span className="text-body-quiet font-normal truncate">
                    {user.email || "-"}
                  </span>
                </div>

                <div className="pl-3">
                  <Icon
                    type="far"
                    name="plus"
                    className="text-current text-base leading-3xl pointer-events-none"
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <div className="w-full border-t border-neutral-quiet p-3 flex items-center justify-start gap-3">
          <Button
            variant="ghost-blue"
            size="xs"
            type="button"
            disabled={isFetching}
            onClick={() => {
              if (isFetching) return;
              fetchNextPage();
            }}
          >
            Más resultados
            <Icon
              type="far"
              name={isFetching ? "spinner-third" : "plus"}
              className={cn("text-current", isFetching && "animate-spin")}
            />
          </Button>
        </div>
      )}
    </>
  );
};
